import { api } from './api';
import { remindersChanged } from './reminders';
import { seriesChanged, type RepeatRule } from './recurrence';
import type { TaskStatus } from './taskStatus';
import type { TaskPriority } from './taskPriority';
import type { Member } from './users';
import type { Assignee } from './assignees';
import type { UploadedFile } from './uploads';

/**
 * Mirrors the server's `toTaskSummary`. Hand-kept in sync, like `Project` --
 * there is no shared types package.
 */
export type TaskSummary = {
  id: string;
  projectId: string;
  /**
   * The project this task lives in, by name as well as id.
   *
   * On every task rather than only the cross-project lists: the server answers
   * every write with the same shape, so a workspace-wide list can write a PATCH
   * reply straight back over its row without the project label disappearing.
   */
  project: { id: string; name: string };
  title: string;
  description: string | null;
  tags: string[];
  status: TaskStatus;
  priority: TaskPriority;
  /** Calendar dates as 'YYYY-MM-DD', never instants. See lib/taskDates.ts. */
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  /** When to be reminded. An ISO instant, not a calendar date like the two
   *  above -- a reminder is a moment. See lib/reminders.ts. */
  remindAt: string | null;
  /** Who the reminder will notify, and the only person it will. Derived
   *  server-side from `remindAt`; compare it against the signed-in user's id to
   *  tell whether a reminder on a shared task is yours. */
  remindSetById: string | null;
  /** How it repeats, or null. See lib/recurrence.ts. */
  repeat: RepeatRule | null;
  /** This instance's neighbours in its series. `nextInstanceId` set means it has
   *  already spawned, and its rule is history -- edits go to the newest one. */
  nextInstanceId: string | null;
  previousInstanceId: string | null;
  /** The one person accountable. */
  owner: Assignee | null;
  /** Everyone else on it, in the order they were added. */
  collaborators: Assignee[];
  createdBy: Member | null;
  /** Which task this one hangs off, if any. `parentId` is what the edit paths
   *  write (see `TaskInput`); `parent` is what the read paths render, and it is
   *  on the summary because a cross-project list cannot look the title up
   *  among its own rows. */
  parentId: string | null;
  parent: TaskRef | null;
  subtaskCount: number;
  commentCount: number;
  attachmentCount: number;
  prerequisiteCount: number;
  /** How many prerequisites are not DONE. Non-zero means the task is blocked. */
  incompletePrerequisiteCount: number;
  /** Both halves, because the row badge reads "2/5" rather than "5". The server
   *  gets the total from a relation count and the done half from a per-page
   *  aggregate -- Prisma's `_count` can only answer once per relation. */
  checklistCount: number;
  checklistDoneCount: number;
  /** Finished entries only; a running timer is added live by the client. */
  trackedSeconds: number;
  createdAt: string;
  updatedAt: string;
};

export type TaskRef = { id: string; title: string; status: TaskStatus };

export type TaskAttachment = {
  id: string;
  taskId: string;
  filename: string;
  contentType: string;
  size: number;
  /** Same-origin path that redirects to a signed URL, so it never expires. */
  downloadUrl: string;
  uploadedBy: Member | null;
  createdAt: string;
};

/** A step inside a task that is not itself a task: a title and a tick, and
 *  deliberately nothing else. Mirrors the server's `toChecklistItemResponse`. */
export type TaskChecklistItem = {
  id: string;
  taskId: string;
  title: string;
  done: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Task = TaskSummary & {
  subtasks: TaskRef[];
  prerequisites: TaskRef[];
  /** What this task is blocking. */
  dependents: TaskRef[];
  attachments: TaskAttachment[];
  /** Embedded on the detail rather than fetched, like attachments: the panel
   *  always shows it, the route caps it at 100, and a tick that waits a round
   *  trip to paint reads as broken rather than slow. */
  checklist: TaskChecklistItem[];
};

export type TaskComment = {
  id: string;
  taskId: string;
  body: string;
  author: Member | null;
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TimeEntry = {
  id: string;
  taskId: string;
  user: Member | null;
  startedAt: string;
  endedAt: string | null;
  /** Null while running. */
  seconds: number | null;
  running: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TaskInput = {
  title: string;
  description: string | null;
  tags: string[];
  status: TaskStatus;
  priority: TaskPriority;
  startDate: string | null;
  dueDate: string | null;
  /** An ISO instant. `remindSetById` is not writable -- the server stamps
   *  whoever sent this. */
  remindAt: string | null;
  /** The whole rule, like `tags`. `null` stops it repeating. */
  repeat: RepeatRule | null;
  ownerId: string | null;
  /** Replaces the whole set, like `tags`. */
  collaboratorIds: string[];
  parentId: string | null;
};

export type TaskSort = 'due' | 'created' | 'updated' | 'title' | 'priority';

export type TaskFilters = {
  status?: TaskStatus[];
  assigneeId?: string;
  tag?: string;
  q?: string;
  sort?: TaskSort;
};

export function listTasks(
  projectId: string,
  filters: TaskFilters,
  signal?: AbortSignal,
): Promise<{ tasks: TaskSummary[] }> {
  const params = new URLSearchParams();
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.q) params.set('q', filters.q);
  if (filters.sort) params.set('sort', filters.sort);
  const query = params.toString();
  return api.get(`/api/projects/${projectId}/tasks${query ? `?${query}` : ''}`, signal);
}

/**
 * A day's work, across every project: everything due on or before `dueTo`, plus
 * everything whose start date has arrived by `startedBy`.
 *
 * Neither bound needs a separate flag for work that is already late or already
 * under way -- yesterday is on or before today, in both directions. The server
 * ORs the two, so a task carrying only one of the two dates still qualifies on
 * that one.
 *
 * Both bounds are the caller's own local day (see lib/taskDates.ts), so what
 * counts as "today" is decided in the one place that knows the reader's
 * timezone. They are passed separately rather than as one date because the
 * route takes them separately: a caller that wants only what is due can still
 * ask for only that.
 */
export function listDayTasks(
  params: { dueTo: string; startedBy?: string; status?: TaskStatus[]; limit?: number },
  signal?: AbortSignal,
): Promise<{ tasks: TaskSummary[]; total: number }> {
  const query = new URLSearchParams({ dueTo: params.dueTo });
  if (params.startedBy) query.set('startedBy', params.startedBy);
  if (params.status?.length) query.set('status', params.status.join(','));
  if (params.limit) query.set('limit', String(params.limit));
  return api.get(`/api/tasks?${query.toString()}`, signal);
}

/**
 * Every task in the workspace, newest filters and all -- what the Tasks page
 * reads.
 *
 * The same endpoint `listDayTasks` uses, minus the date bound: without `dueTo`
 * the server does not constrain the date at all, so undated tasks come back
 * too. Kept as its own function rather than folded into `listDayTasks` because
 * the two ask genuinely different questions, and a single call with half its
 * arguments unset reads as neither.
 *
 * `total` is the unpaginated count, so a truncated list can offer to fetch more.
 */
export function listAllTasks(
  filters: TaskFilters & { projectId?: string; limit?: number },
  signal?: AbortSignal,
): Promise<{ tasks: TaskSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.q) params.set('q', filters.q);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  return api.get(`/api/tasks${query ? `?${query}` : ''}`, signal);
}

export const listTaskTags = (projectId: string, signal?: AbortSignal) =>
  api.get<{ tags: string[] }>(`/api/projects/${projectId}/tasks/tags`, signal);

/** The same suggestions across every project, for the Tasks page's tag filter. */
export const listAllTaskTags = (signal?: AbortSignal) =>
  api.get<{ tags: string[] }>('/api/tasks/tags', signal);

export const getTask = (taskId: string, signal?: AbortSignal) =>
  api.get<{ task: Task }>(`/api/tasks/${taskId}`, signal);

/**
 * The two writes below tell the reminder scheduler when they touched a reminder.
 *
 * Here rather than in the forms because these are the only two ways a reminder
 * can change, and the alternative is every panel that edits a task knowing a
 * scheduler exists. Only when the key was actually sent: an ordinary edit must
 * not cost a refetch.
 */
const announceReminder = async <T,>(input: Partial<TaskInput>, write: Promise<T>): Promise<T> => {
  const result = await write;
  if (input.remindAt !== undefined) remindersChanged();
  return result;
};

/**
 * What a write answers with when it also brought on, or took away, the next
 * instance of a repeating task -- finishing one spawns, and the Undo of that
 * takes it back.
 */
export type TaskWriteResult = {
  task: Task;
  /** The instance this write spawned. */
  spawned?: Task;
  /** The instance this write took away. */
  removedInstanceId?: string;
};

/**
 * The same choke point as the reminder one above, for a series: either key on
 * the reply means a list somewhere may have gained or lost a row it never saw
 * this write touch. A spawned instance carries the reminder forward too, so the
 * scheduler hears about it.
 */
const announceSeries = async (write: Promise<TaskWriteResult>): Promise<TaskWriteResult> => {
  const result = await write;
  if (result.spawned || result.removedInstanceId) {
    seriesChanged();
    if (result.spawned?.remindAt || result.removedInstanceId) remindersChanged();
  }
  return result;
};

export const createTask = (projectId: string, input: Partial<TaskInput> & { title: string }) =>
  announceSeries(
    announceReminder(input, api.post<TaskWriteResult>(`/api/projects/${projectId}/tasks`, input)),
  );

/**
 * A task that has been asked for but not yet answered for.
 *
 * Creating one is a round trip to Frankfurt and back, and making somebody watch
 * a spinner for it is what this exists to avoid: the row goes into the list
 * straight away under one of these ids and is swapped for the server's row when
 * it lands. The prefix is what tells them apart -- a real id is always a uuid,
 * so no row can be mistaken for pending and no pending row can be mistaken for
 * something the API would recognise.
 *
 * Counted rather than random: `crypto.randomUUID` is unavailable outside a
 * secure context, and nothing here needs to be unguessable -- these ids live in
 * one tab's memory for about a second.
 */
const PENDING_PREFIX = 'pending:';
let pendingCount = 0;

export const nextPendingTaskId = (): string => `${PENDING_PREFIX}${++pendingCount}`;

/** True while the row is still in flight, so nothing links to or edits it. */
export const isPendingTask = (taskId: string): boolean => taskId.startsWith(PENDING_PREFIX);

/**
 * What the list shows for a task the server has not confirmed yet.
 *
 * Every count is zero and every timestamp is now, which is exactly right for a
 * task that has just been typed: it cannot have comments, subtasks or logged
 * time, and the real row replaces this one before any of that can change.
 *
 * `completedAt` follows the status the same way the server derives it, so a task
 * created straight into Done does not flicker from unfinished to finished when
 * the real row arrives.
 */
export function optimisticTask(
  id: string,
  project: { id: string; name: string },
  input: TaskInput,
  people: { owner: Assignee | null; collaborators: Assignee[]; createdBy: Member | null },
): TaskSummary {
  const now = new Date().toISOString();

  return {
    id,
    projectId: project.id,
    // Carried on the optimistic row too, so a task typed on the Tasks page does
    // not sit there unlabelled until the server answers for it.
    project,
    title: input.title,
    description: input.description,
    tags: input.tags,
    status: input.status,
    priority: input.priority,
    startDate: input.startDate,
    dueDate: input.dueDate,
    completedAt: input.status === 'DONE' ? now : null,
    remindAt: input.remindAt,
    // Left null rather than guessed at from the signed-in user. The row carries
    // no control that reads it, and the reply that fills it in lands before
    // anything could.
    remindSetById: null,
    repeat: input.repeat,
    nextInstanceId: null,
    previousInstanceId: null,
    owner: people.owner,
    collaborators: people.collaborators,
    createdBy: people.createdBy,
    parentId: input.parentId,
    // The caller chose a parent id but has no title for it, and inventing one
    // here would mean a second place that decides what a parent looks like.
    // The row goes without its breadcrumb for the second before the reply lands,
    // the same way its counts sit at zero.
    parent: null,
    subtaskCount: 0,
    commentCount: 0,
    attachmentCount: 0,
    prerequisiteCount: 0,
    incompletePrerequisiteCount: 0,
    checklistCount: 0,
    checklistDoneCount: 0,
    trackedSeconds: 0,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * What a task list row is allowed to change inline: the fields a row actually
 * shows. Narrower than `Partial<TaskInput>` on purpose -- it is what keeps the
 * optimistic apply below a small spread, where the panel's `withPatch` has to
 * resolve owners, collaborators and parents against lookup tables first.
 */
export type RowPatch = Partial<Pick<TaskInput, 'status' | 'priority' | 'dueDate'>>;

/**
 * A row patch applied to the row already on screen, for the moment before the
 * server answers.
 *
 * `completedAt` follows the status here the same way `optimisticTask` sets it
 * and the same way the server derives it -- it is never sent, only mirrored, so
 * a task marked done does not read as unfinished until the reply lands.
 */
export function applyRowPatch(task: TaskSummary, patch: RowPatch): TaskSummary {
  const status = patch.status;

  return {
    ...task,
    ...patch,
    ...(status === undefined || status === task.status
      ? {}
      : status === 'DONE'
        ? { completedAt: new Date().toISOString() }
        : task.status === 'DONE'
          ? { completedAt: null }
          : {}),
  };
}

/**
 * A list row shown as a full task, using whatever relations the cache has.
 *
 * `Task` is `TaskSummary` plus five relation keys, so a row the list is already
 * holding covers everything the panel's header, description and property sheet
 * need. Rendering from it is what lets the panel open filled in rather than on
 * a spinner, with only the relation sections still to arrive.
 *
 * The summary fields come from the row and not from `cached` on purpose: the
 * list is patched optimistically on every edit, so it is never the staler of
 * the two. That is what keeps the cache free of any merge or invalidation
 * rules -- it is only ever asked for the parts the list does not have.
 *
 * Empty relations here are indistinguishable from genuinely empty ones, so the
 * caller has to track whether the detail has landed separately; it cannot read
 * that back off the result.
 */
export function hydrateSeed(seed: TaskSummary, cached: Task | undefined): Task {
  return {
    ...seed,
    parent: cached?.parent ?? null,
    subtasks: cached?.subtasks ?? [],
    prerequisites: cached?.prerequisites ?? [],
    dependents: cached?.dependents ?? [],
    attachments: cached?.attachments ?? [],
    checklist: cached?.checklist ?? [],
  };
}

/** PATCH semantics match projects: a key left out is unchanged, `null` clears. */
export const updateTask = (taskId: string, input: Partial<TaskInput>) =>
  announceSeries(announceReminder(input, api.patch<TaskWriteResult>(`/api/tasks/${taskId}`, input)));

export const deleteTask = (taskId: string) =>
  api.del<{ ok: true; reparentedCount: number }>(`/api/tasks/${taskId}`);

export const addPrerequisite = (taskId: string, prerequisiteId: string) =>
  api.post<{ task: Task }>(`/api/tasks/${taskId}/prerequisites`, { prerequisiteId });

export const removePrerequisite = (taskId: string, prerequisiteId: string) =>
  api.del<{ task: Task }>(`/api/tasks/${taskId}/prerequisites/${prerequisiteId}`);

export const listComments = (taskId: string, signal?: AbortSignal) =>
  api.get<{ comments: TaskComment[] }>(`/api/tasks/${taskId}/comments`, signal);

export const addComment = (taskId: string, body: string) =>
  api.post<{ comment: TaskComment }>(`/api/tasks/${taskId}/comments`, { body });

export const updateComment = (taskId: string, commentId: string, body: string) =>
  api.patch<{ comment: TaskComment }>(`/api/tasks/${taskId}/comments/${commentId}`, { body });

export const deleteComment = (taskId: string, commentId: string) =>
  api.del<{ ok: true }>(`/api/tasks/${taskId}/comments/${commentId}`);

/** The bytes are already in S3 by this point; this records the row. */
export const addAttachment = (taskId: string, file: UploadedFile) =>
  api.post<{ attachment: TaskAttachment }>(`/api/tasks/${taskId}/attachments`, file);

export const deleteAttachment = (taskId: string, attachmentId: string) =>
  api.del<{ ok: true }>(`/api/tasks/${taskId}/attachments/${attachmentId}`);

/** Unused by the app -- the checklist rides on the task detail, the same way
 *  attachments do. Here because the endpoint exists and a caller should not have
 *  to hand-roll the path. */
export const listChecklist = (taskId: string, signal?: AbortSignal) =>
  api.get<{ items: TaskChecklistItem[] }>(`/api/tasks/${taskId}/checklist`, signal);

export const addChecklistItem = (taskId: string, title: string) =>
  api.post<{ item: TaskChecklistItem }>(`/api/tasks/${taskId}/checklist`, { title });

/** PATCH semantics as everywhere else: a key left out is unchanged. */
export const updateChecklistItem = (
  taskId: string,
  itemId: string,
  patch: { title?: string; done?: boolean },
) => api.patch<{ item: TaskChecklistItem }>(`/api/tasks/${taskId}/checklist/${itemId}`, patch);

export const deleteChecklistItem = (taskId: string, itemId: string) =>
  api.del<{ ok: true }>(`/api/tasks/${taskId}/checklist/${itemId}`);

/**
 * Put the checklist in this order.
 *
 * The whole order, not a move: "item X to index 3" only means anything against
 * the list the caller was looking at, where this is idempotent and says what
 * the answer should be rather than how it was arrived at. The server answers
 * with the whole list, because a caller that dragged one row has no way to know
 * what happened to the rest.
 *
 * Ids left out keep their relative order at the end, which is what lets a
 * client reorder while a create is still in flight.
 */
export const reorderChecklist = (taskId: string, itemIds: string[]) =>
  api.put<{ items: TaskChecklistItem[] }>(`/api/tasks/${taskId}/checklist/order`, { itemIds });

export const listTimeEntries = (taskId: string, signal?: AbortSignal) =>
  api.get<{ entries: TimeEntry[]; totalSeconds: number }>(
    `/api/tasks/${taskId}/time-entries`,
    signal,
  );

/**
 * Whatever the caller has running, wherever it is.
 *
 * The server allows one running timer per user app-wide, so this is the only
 * way a list of tasks can tell which of its rows -- if any -- is the one being
 * timed, without asking once per row.
 */
export const getRunningTimer = (signal?: AbortSignal) =>
  api.get<{ entry: TimeEntry | null; task: TaskRef | null }>(
    '/api/time-entries/running',
    signal,
  );

/** The server stamps `startedAt` and refuses a second running timer. */
export const startTimer = (taskId: string) =>
  api.post<{ entry: TimeEntry }>(`/api/tasks/${taskId}/time-entries/start`);

export const stopTimer = (taskId: string) =>
  api.post<{ entry: TimeEntry }>(`/api/tasks/${taskId}/time-entries/stop`);

export const addTimeEntry = (
  taskId: string,
  input: { startedAt: string; seconds: number; note?: string | null },
) => api.post<{ entry: TimeEntry }>(`/api/tasks/${taskId}/time-entries`, input);

export const deleteTimeEntry = (taskId: string, entryId: string) =>
  api.del<{ ok: true }>(`/api/tasks/${taskId}/time-entries/${entryId}`);

/* -- derivations the list already holds the data for ---------------------- */

export const isBlocked = (task: TaskSummary): boolean => task.incompletePrerequisiteCount > 0;

/**
 * Every task beneath `rootId`, so the parent picker cannot offer a task its own
 * descendant. The server rejects a cycle anyway; this keeps the UI from
 * offering a choice it knows will fail.
 */
export function descendantIds(rootId: string, tasks: TaskSummary[]): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const task of tasks) {
    if (!task.parentId) continue;
    childrenByParent.set(task.parentId, [...(childrenByParent.get(task.parentId) ?? []), task.id]);
  }

  const seen = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    for (const child of childrenByParent.get(queue.pop()!) ?? []) {
      // Guarded against a cycle in bad data, which would otherwise hang the tab.
      if (seen.has(child)) continue;
      seen.add(child);
      queue.push(child);
    }
  }
  return seen;
}
